/**
 * The pending origin of a `forecast.open` (P2 design §4.6). Each entry point that focuses a
 * forecast says how before it focuses one — a forecast-manager row (`manager` / `rail`), the
 * chat history (`chat`), a featured card on the new-question page (`featured`), the phone list
 * (`phone`), a deep link (`link` / `alert`), boot and the resume sweep (`auto`) — and
 * hooks/useForecastOpenIntent.js emits the intent at the focus transition with that `from`.
 * A focus change with no pending origin (a new forecast's own run going active, a branch, the
 * tour) is no `forecast.open`. A pending origin that no focus change consumes expires.
 */

/** A pending origin older than this is stale: whatever focus change follows is not its. */
export const PENDING_OPEN_MS = 5_000;

let pending = null;

const monoNow = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

/**
 * Note how the next focus change happens. `keep` leaves an origin already pending in place
 * (a row handler that a deep link reuses must not overwrite the link's own origin).
 */
export function noteForecastOpen(from, { keep = false, now = monoNow } = {}) {
  if (typeof from !== "string" || !from) return;
  const t = now();
  if (keep && pending && t - pending.at <= PENDING_OPEN_MS) return;
  pending = { from, at: t };
}

/** The pending origin, consumed: `from`, or null when there is none (or it expired). */
export function takeForecastOpen({ now = monoNow } = {}) {
  const p = pending;
  pending = null;
  if (!p || now() - p.at > PENDING_OPEN_MS) return null;
  return p.from;
}

/** Forget any pending origin (tests). */
export function resetForecastOpen() {
  pending = null;
}
